import { DataProp } from 'editorjs-blocks-react-renderer'

import { api } from '../../configuration'

type StrapiTextChild = {
  type?: 'text'
  text?: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
  strikethrough?: boolean
  code?: boolean
}

type StrapiLinkChild = {
  type: 'link'
  url: string
  children?: StrapiNode[]
}

type StrapiListItem = {
  type: 'list-item'
  children?: StrapiNode[]
}

type StrapiNode =
  | StrapiTextChild
  | StrapiLinkChild
  | StrapiListItem
  | {
      type: 'paragraph' | 'heading' | 'quote' | 'code'
      level?: number
      children?: StrapiNode[]
    }
  | {
      type: 'list'
      format?: 'ordered' | 'unordered'
      children?: StrapiListItem[]
    }
  | {
      type: 'image'
      image?: { url?: string; alternativeText?: string }
    }

const escapeHtml = (str: string) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const childrenToHtml = (nodes?: StrapiNode[]): string => {
  if (!Array.isArray(nodes)) return ''
  return nodes
    .map((node) => {
      if (!node) return ''
      if ((node as StrapiLinkChild).type === 'link') {
        const link = node as StrapiLinkChild
        return `<a href="${escapeHtml(
          link.url ?? ''
        )}" target="_blank" rel="noopener noreferrer">${childrenToHtml(
          link.children
        )}</a>`
      }
      if (
        !('type' in node) ||
        node.type === 'text' ||
        node.type === undefined
      ) {
        const textNode = node as StrapiTextChild
        let text = escapeHtml(textNode.text ?? '')
        if (textNode.bold) text = `<b>${text}</b>`
        if (textNode.italic) text = `<i>${text}</i>`
        if (textNode.underline) text = `<u>${text}</u>`
        if (textNode.strikethrough) text = `<s>${text}</s>`
        if (textNode.code) text = `<code>${text}</code>`
        return text
      }
      return ''
    })
    .join('')
}

const strapiBlocksToEditorJs = (blocks: StrapiNode[]): DataProp => {
  const editorBlocks = blocks
    .map((block): DataProp['blocks'][number] | null => {
      if (!block || !('type' in block)) return null
      switch (block.type) {
        case 'paragraph':
          return {
            type: 'paragraph',
            data: { text: childrenToHtml(block.children) },
          } as any
        case 'heading':
          return {
            type: 'header',
            data: {
              text: childrenToHtml(block.children),
              level: block.level ?? 2,
            },
          } as any
        case 'list': {
          const listBlock = block as Extract<StrapiNode, { type: 'list' }>
          return {
            type: 'list',
            data: {
              style: listBlock.format === 'ordered' ? 'ordered' : 'unordered',
              items: (listBlock.children ?? []).map((li) =>
                childrenToHtml(li.children)
              ),
            },
          } as any
        }
        case 'quote':
          return {
            type: 'quote',
            data: {
              text: childrenToHtml(block.children),
              caption: '',
              alignment: 'left',
            },
          } as any
        case 'code':
          return {
            type: 'code',
            data: { code: childrenToHtml(block.children) },
          } as any
        case 'image': {
          const imgBlock = block as Extract<StrapiNode, { type: 'image' }>
          const url = imgBlock.image?.url
            ? imgBlock.image.url.startsWith('http')
              ? imgBlock.image.url
              : api.API_URL + imgBlock.image.url
            : ''
          return {
            type: 'image',
            data: {
              file: { url },
              caption: imgBlock.image?.alternativeText ?? '',
              withBackground: false,
              withBorder: false,
              stretched: false,
            },
          } as any
        }
        default:
          return null
      }
    })
    .filter(Boolean) as DataProp['blocks']

  return {
    blocks: editorBlocks,
    time: Date.now(),
    version: '2.0.0',
  }
}

/**
 * API expects classified `description` as an Editor.js JSON string.
 * Form state may still hold Strapi blocks (array) if the editor was loaded from GET but not edited.
 */
export const descriptionToEditorJsString = (description: unknown): string => {
  if (description == null || description === '') {
    return ''
  }
  if (typeof description === 'string') {
    return description
  }
  if (Array.isArray(description)) {
    const editorData = transformRichText(description as StrapiNode[])
    return JSON.stringify(
      editorData ?? { blocks: [], time: Date.now(), version: '2.0.0' }
    )
  }
  if (
    typeof description === 'object' &&
    description !== null &&
    'blocks' in description &&
    Array.isArray((description as DataProp).blocks)
  ) {
    return JSON.stringify(description)
  }
  return ''
}

export const transformRichText = (
  input?: string | StrapiNode[] | null
): DataProp | undefined => {
  if (!input) return undefined

  if (Array.isArray(input)) {
    try {
      return strapiBlocksToEditorJs(input)
    } catch (e) {
      console.log('transformRichText blocks error:', e)
      return undefined
    }
  }

  try {
    const parsed = JSON.parse(input)

    if (Array.isArray(parsed)) {
      return strapiBlocksToEditorJs(parsed as StrapiNode[])
    }

    const json: DataProp = parsed as DataProp
    json.blocks = json.blocks.map((block) => {
      if (block.type === 'image') {
        block.data.file.url = api.API_URL + block.data.file.url
      }
      return block
    })
    return json
  } catch (e) {
    console.log(e, input)
  }
  return undefined
}
